/**
 * Bracket geometry and seeding.
 *
 * Deliberately pure: no React, no DOM, no styling. The tool draws the result as
 * SVG and the print stylesheet scales that SVG to the sheet, so every number
 * here is in abstract user units rather than pixels or millimetres.
 *
 * THE PRODUCT RULE THIS FILE ENCODES: only round one ever carries names.
 * Every later round is returned as empty slots, because the whole point of the
 * printable bracket is that somebody fills the rest in by hand. The widget is
 * what advances winners automatically. See the plan report from 2026-09-15.
 */

/**
 * The elimination formats this sheet can draw.
 *
 * `single`      - lose once, you are out.
 * `double`      - a first loss drops you into a losers bracket.
 * `consolation` - only the FIRST-ROUND losers get a second bracket. Not the
 *                 same as double elimination, and the difference matters: it
 *                 guarantees everyone two games without doubling the schedule,
 *                 which is why school and youth tournaments use it.
 * `triple`      - three losses to be out. Rare, and used where people have
 *                 travelled a long way for the day.
 */
export type TBracketFormat = 'single' | 'double' | 'consolation' | 'triple';
export type TDrawOrder = 'index' | 'random' | 'seeding';

/**
 * What goes on the first-round lines.
 *
 * `names`  - the entrants, as typed. The default.
 * `seeds`  - the seed numbers only, lines left empty for handwriting.
 * `blank`  - nothing at all, an empty sheet.
 *
 * `blank` is not a nicety: "blank bracket", "blank tournament bracket",
 * "empty bracket template" and "printable blank bracket" are about 3,310 US
 * searches a month between them, and neither CommonNinja nor BracketsNinja
 * ranks for any of them today.
 */
export type TFillMode = 'names' | 'seeds' | 'blank';
export type TBracketSize = 4 | 8 | 16 | 32 | 64;

/** Widget parity: `MinimumParticipantsPerBracketType` is 4 for both formats. */
export const MIN_ENTRANTS = 2;
export const BRACKET_SIZES: TBracketSize[] = [4, 8, 16, 32, 64];

/**
 * 64 is the cap.
 *
 * It was 32 on my recommendation, on the grounds that a bigger draw stops being
 * legible on one sheet. Sergei overruled that on 2026-09-15 and he is right that
 * the demand is there: "64 team bracket" and its variants are about 940 US
 * searches a month, and a 64-slot sheet is the standard shape for an office
 * pool.
 *
 * The legibility concern was real though, so it is handled rather than ignored.
 * 64 rows across a 190mm page leaves 2.97mm per row, which is about 5.5pt text:
 * printable, but tight. `slotPitchFor` and `fontSizeFor` below are tuned to get
 * as close to that physical ceiling as possible, the tool reports the print size
 * before you commit paper to it, and a 64 draw can be split over two sheets.
 */
export const MAX_ENTRANTS = 64;

export interface IEntrant {
  id: string;
  name: string;
}

/** A single writable line on the sheet. */
export interface ISlot {
  /** Empty string means a blank line to be filled in by hand. */
  label: string;
  isBye: boolean;
  /** Seed number to print in the gutter, when seeding is the chosen draw. */
  seed: number | null;
}

/**
 * A fixed, real-world draw that a generic seeded tree cannot express.
 *
 * ⚠️ Added 2026-09-17 for the NFL playoff sheet, the first EVENT variant. The
 * family was built on `seedOrder`, which spreads one pool of seeds across the
 * whole tree so the top two meet last. That is right for one pool and wrong for
 * the NFL, which is two separate conference draws of seven that only meet in
 * the Super Bowl. Seeded generically, AFC and NFC teams interleave and the
 * sheet pairs teams that can never play each other.
 *
 * So an event variant supplies its own first-round slot map and its own column
 * headers, and the engine draws the same tree around them. Everything here is
 * optional: with no plan the generic seeding is untouched.
 */
export interface IEventPlan {
  /** Entrant index per first-round slot, null for a bye. Length === bracket size. */
  slotPlan: (number | null)[];
  /** Seed number printed in the gutter per slot, null for none. */
  slotSeeds: (number | null)[];
  /** Column headers, one per round, replacing "Round of 16" and friends. */
  roundNames: string[];
  /** Header above the final blank line, replacing "Champion". */
  championLabel: string;
  /**
   * Labels for the top and bottom half of the draw, printed down the left edge.
   * This is what stops the sheet being an anonymous 16-team tree: without it
   * nothing on paper says which half is the AFC.
   */
  halfLabels?: [string, string];
  /**
   * What one half of the draw is CALLED, for the panel copy that has to name it
   * ("12 teams, 6 in each conference").
   *
   * ⚠️ Added 2026-09-22 with the College Football Playoff sheet, the second
   * event variant. The NFL wording was written straight into the tool, so the
   * CFP sheet would have told a visitor its twelve teams were "6 in each
   * conference", which the CFP does not have: it is one pool of twelve seeded
   * 1 to 12. A draw with no meaningful halves simply leaves this unset and the
   * copy drops the clause instead of inventing a structure.
   */
  halfNoun?: string;
}

export interface IMatch {
  slots: [ISlot, ISlot];
  /** Vertical centre of the match, in user units. */
  centre: number;
  /**
   * The actual y of this match's two writable lines.
   *
   * NOT derivable from `centre` and the slot pitch. Only in round one are a
   * match's two lines one pitch apart; in round two they are two pitches apart,
   * in round three four, and so on, because each line sits on the centre of the
   * match that feeds it. Deriving them was a real bug: every connector past
   * round one was drawn to the wrong height and met nothing.
   */
  lineYs: [number, number];
}

export interface IRound {
  /** Short column code, printed above the column: L1, C2, S3, T1. */
  name: string;
  /**
   * The section this column belongs to, printed as a band above the columns.
   *
   * ⚠️ Added 2026-09-16 to fix a real defect. The second sheet used to label
   * every column `L${index + 1}` whatever the format was, because the renderer
   * built the label from the column index and threw `name` away. The result:
   * the consolation draw and the third-chance draw both printed as losers
   * rounds, so double, consolation and triple elimination produced three sheets
   * that were impossible to tell apart. Sergei spotted it by eye.
   */
  group?: string;
  matches: IMatch[];
}

export interface IBracketLayout {
  rounds: IRound[];
  /** Blank losers-bracket rounds, only for the double-elimination sheet. */
  losersRounds: IRound[];
  /**
   * Height the losers bracket actually needs, including its labels.
   *
   * Computed here rather than guessed by the renderer. Guessing it clipped the
   * bottom two rows of every double-elimination sheet.
   */
  losersHeight: number;
  /** Y centre of the champion line. */
  championCentre: number;
  width: number;
  height: number;
  slotPitch: number;
  /**
   * Column width, chosen to fill the paper rather than fixed.
   *
   * The height of a bracket is decided by the player count, but the width is
   * free. Left at a constant, a 32-player sheet fitted to A4 landscape
   * letterboxed and wasted about 15% of the page. Widening the columns until
   * the drawing's aspect matches the paper's costs nothing (the fit is still
   * height-limited, so the text does not shrink) and it buys longer lines to
   * write the winners' names on, which is the entire job of the sheet.
   */
  colW: number;
  /** Y centre of the optional third-place line, or null when it is off. */
  thirdPlaceCentre: number | null;
}

/** Floor for a column. Below this a name has nowhere to go. */
export const COL_W = 150;
/** Ceiling, so a tiny bracket does not become a page of stretched whitespace. */
export const COL_W_MAX = 300;
export const GUTTER = 26;
export const HEADER_H = 30;

/**
 * Picks a column width so the drawing fills the paper.
 *
 * `contentHeight` is fixed by the draw. Solving for the width that makes the
 * content's aspect equal the page's, then clamping, gives the widest usable
 * lines that still fit on one sheet.
 */
export function columnWidthForPage(
  columns: number,
  contentHeight: number,
  pageAspect: number,
  minColW: number = COL_W
): number {
  if (!columns || !contentHeight || !pageAspect) return minColW;
  const targetWidth = contentHeight * pageAspect;
  const perColumn = targetWidth / columns - GUTTER;
  return Math.round(Math.max(minColW, Math.min(COL_W_MAX, perColumn)));
}

/**
 * Floor for a column on a mirrored sheet, which is a different trade.
 *
 * A left-to-right sheet is height-limited: it runs out of paper vertically
 * first, so the columns can afford to be wide. A mirrored sheet is eleven
 * columns across and only half as tall, so it runs out of paper HORIZONTALLY,
 * and holding the 150-unit floor would shrink the whole drawing to about 4.5pt
 * to make the width fit. Letting the columns narrow instead keeps the text at
 * a readable size and costs only the length of the writing line, which a
 * college name fits inside anyway.
 *
 * ⚠️ 104, not the 84 this started at. 84 fitted the page and printed larger,
 * but it cut every name at nine characters, which is short enough to lose the
 * region off "Midwest 12" in the worked example. The floor is set by what a
 * line has to be able to SAY, not only by what fits: 104 still prints near
 * seven points on A4 landscape, comfortably over the legibility floor.
 */
export const COL_W_MIRRORED_MIN = 104;

/** Tighter lines as the bracket grows, so even 64 entrants fit one sheet. */
function slotPitchFor(size: TBracketSize): number {
  if (size >= 64) return 16;
  if (size >= 32) return 24;
  if (size >= 16) return 30;
  if (size >= 8) return 34;
  return 38;
}

/**
 * Text size, as a share of the row rather than a fixed number.
 *
 * The old rule (`slotPitch - 13`) collapsed onto its floor of 9 once the rows
 * got tight, so a 64 draw printed at about 4.3pt where the row could have
 * carried 5.5pt. Keeping the text proportional to the row uses the space that
 * is actually there.
 */
export function fontSizeFor(slotPitch: number): number {
  return Math.round(Math.min(13, Math.max(8, slotPitch * 0.68)));
}

export function isPowerOfTwo(n: number): boolean {
  return n >= 2 && (n & (n - 1)) === 0;
}

/** Smallest legal bracket size that holds this many entrants. */
export function sizeForEntrants(count: number): TBracketSize {
  const found = BRACKET_SIZES.find((s) => s >= count);
  return found ?? 64;
}

/**
 * Standard tournament seeding order.
 *
 * Returns seed numbers in slot order, so index 0 is the top line of the sheet.
 * Built by repeated reflection, which is the conventional construction: 1 v 2
 * becomes 1 v 4, 2 v 3, becomes 1 v 8, 4 v 5, 2 v 7, 3 v 6, and so on. The
 * effect is that the top seeds meet as late as possible.
 */
export function seedOrder(size: number): number[] {
  let order = [1, 2];
  while (order.length < size) {
    const total = order.length * 2 + 1;
    const next: number[] = [];
    for (const seed of order) {
      next.push(seed);
      next.push(total - seed);
    }
    order = next;
  }
  return order;
}

/** Deterministic shuffle so a redraw is reproducible within one click. */
function shuffle<T>(items: T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Places entrants onto the first-round lines.
 *
 * Fewer entrants than slots means byes. Under seeding the byes fall to the
 * lowest seeds automatically, which is what gives the top seeds a free first
 * round exactly as a real draw would. Under the other two orders the byes are
 * simply the leftover lines at the bottom.
 */
export function placeEntrants(
  entrants: IEntrant[],
  size: TBracketSize,
  order: TDrawOrder,
  fill: TFillMode = 'names',
  plan?: IEventPlan
): ISlot[] {
  const blank = (): ISlot => ({ label: '', isBye: true, seed: null });

  /**
   * An event plan overrides the draw order entirely.
   *
   * The slot map IS the draw, so `order` has nothing left to decide: there is
   * no random NFL playoff bracket. A blank fill still blanks everything, and a
   * seeds fill keeps the plan's seed numbers, because on this sheet a seed
   * number means "the 4 seed", not "the fourth line".
   */
  if (plan) {
    return plan.slotPlan.map((entrantIndex, i) => {
      const seed = plan.slotSeeds[i] ?? null;
      if (fill === 'blank') return { label: '', isBye: false, seed: null };
      if (entrantIndex === null) return { label: '', isBye: true, seed: null };
      if (fill === 'seeds') return { label: '', isBye: false, seed };
      const entrant = entrants[entrantIndex];
      if (!entrant) return { label: '', isBye: false, seed };
      return { label: entrant.name, isBye: false, seed };
    });
  }

  // An entirely empty sheet. No names, no seeds, and crucially no "bye"
  // markers: on a blank sheet nobody has been drawn yet, so there is nothing
  // to have a bye from.
  if (fill === 'blank') {
    return Array.from({ length: size }, () => ({
      label: '',
      isBye: false,
      seed: null,
    }));
  }

  // Numbered lines to write names onto. The seed order is the real one, so the
  // sheet still pairs 1 against the bottom seed exactly as a drawn bracket does.
  if (fill === 'seeds') {
    return seedOrder(size).map((seed) => ({
      label: '',
      isBye: false,
      seed,
    }));
  }

  if (order === 'seeding') {
    const bySeed = entrants.slice(0, size);
    return seedOrder(size).map((seed) => {
      const entrant = bySeed[seed - 1];
      if (!entrant) return blank();
      return { label: entrant.name, isBye: false, seed };
    });
  }

  const ordered = order === 'random' ? shuffle(entrants) : entrants;
  const slots: ISlot[] = [];
  for (let i = 0; i < size; i += 1) {
    const entrant = ordered[i];
    slots.push(
      entrant ? { label: entrant.name, isBye: false, seed: null } : blank()
    );
  }
  return slots;
}

/**
 * Grows a knockout tree upward from a set of filled first-round lines.
 *
 * ⚠️ Extracted 2026-09-22, unchanged, so the mirrored sheet can build each of
 * its two halves with the same geometry the straight sheet uses. It is the
 * subtlest arithmetic in the file and having two copies of it would have been
 * the kind of duplication that silently drifts: `lineYs` is NOT derivable from
 * `centre` and the pitch, because only in round one are a match's two lines one
 * pitch apart. Deriving them drew every connector past round one to the wrong
 * height. One copy, two callers.
 */
function buildTree(
  firstRoundSlots: ISlot[],
  slotPitch: number,
  nameFor: (roundIndex: number, matchCount: number) => string
): { rounds: IRound[]; championCentre: number } {
  const size = firstRoundSlots.length;

  // Line positions for round one, evenly pitched down the sheet.
  const lineY: number[] = [];
  for (let i = 0; i < size; i += 1) {
    lineY.push(i * slotPitch + slotPitch / 2);
  }

  const roundCount = Math.log2(size);
  const rounds: IRound[] = [];
  let currentLines = lineY;

  for (let r = 0; r < roundCount; r += 1) {
    const matchCount = currentLines.length / 2;
    const matches: IMatch[] = [];
    const nextLines: number[] = [];

    for (let m = 0; m < matchCount; m += 1) {
      const topY = currentLines[m * 2];
      const bottomY = currentLines[m * 2 + 1];
      const centre = (topY + bottomY) / 2;

      const slots: [ISlot, ISlot] =
        r === 0
          ? [firstRoundSlots[m * 2], firstRoundSlots[m * 2 + 1]]
          : [
              { label: '', isBye: false, seed: null },
              { label: '', isBye: false, seed: null },
            ];

      matches.push({ slots, centre, lineYs: [topY, bottomY] });
      nextLines.push(centre);
    }

    rounds.push({ name: nameFor(r, matchCount), matches });
    currentLines = nextLines;
  }

  return { rounds, championCentre: currentLines[0] };
}

function roundName(matchesInRound: number): string {
  if (matchesInRound === 1) return 'Final';
  if (matchesInRound === 2) return 'Semifinals';
  if (matchesInRound === 4) return 'Quarterfinals';
  return `Round of ${matchesInRound * 2}`;
}

/**
 * A blank knockout tree of `size` slots, laid out as evenly spaced columns.
 *
 * Used for every secondary bracket: the consolation draw, and the second and
 * third chance draws in triple elimination. Every line is empty by design,
 * because who drops into a secondary bracket cannot be known before the day.
 */
function blankTree(
  size: number,
  slotPitch: number,
  label: string,
  group: string
): IRound[] {
  const rounds: IRound[] = [];
  let matches = Math.floor(size / 2);
  let r = 0;
  while (matches >= 1) {
    rounds.push({
      name: `${label}${r + 1}`,
      group,
      matches: Array.from({ length: matches }, (_, m) => {
        const centre = m * (slotPitch * 3) + slotPitch;
        return {
          slots: [
            { label: '', isBye: false, seed: null },
            { label: '', isBye: false, seed: null },
          ] as [ISlot, ISlot],
          centre,
          lineYs: [centre - slotPitch / 2, centre + slotPitch / 2] as [
            number,
            number,
          ],
        };
      }),
    });
    if (matches === 1) break;
    matches = Math.floor(matches / 2);
    r += 1;
  }
  return rounds;
}

/**
 * Blank losers-bracket rounds for the double-elimination sheet.
 *
 * A double-elimination draw of N has 2*log2(N) - 2 losers rounds, and the match
 * counts step down in pairs: N/4, N/4, N/8, N/8, and so on. Every line is blank
 * by design. Nobody can know who drops into a losers bracket until the day.
 */
function losersRoundCounts(size: TBracketSize): number[] {
  const k = Math.log2(size);
  const counts: number[] = [];
  for (let j = 1; j <= k - 1; j += 1) {
    const matches = size / Math.pow(2, j + 1);
    counts.push(matches, matches);
  }
  return counts;
}

/**
 * Builds the whole sheet.
 *
 * Round one takes the placed slots. Every later round is generated blank, and
 * each of its lines sits at the vertical centre of the match that feeds it, so
 * the connectors meet the lines exactly.
 */
export function buildLayout({
  entrants,
  size,
  order,
  format,
  thirdPlace,
  pageAspect,
  fill = 'names',
  plan,
}: {
  entrants: IEntrant[];
  size: TBracketSize;
  order: TDrawOrder;
  format: TBracketFormat;
  thirdPlace: boolean;
  fill?: TFillMode;
  /** Printable width divided by printable height of the chosen paper. */
  pageAspect?: number;
  /** A fixed real-world draw. See IEventPlan. */
  plan?: IEventPlan;
}): IBracketLayout {
  const slotPitch = slotPitchFor(size);
  const firstRoundSlots = placeEntrants(entrants, size, order, fill, plan);

  const { rounds, championCentre } = buildTree(
    firstRoundSlots,
    slotPitch,
    (r, matchCount) => plan?.roundNames[r] ?? roundName(matchCount)
  );

  const roundCount = rounds.length;
  const height = size * slotPitch;

  // One column per round, plus the champion column.
  // Recomputed below once the column width is known.

  // Which secondary bracket, if any, sits under the main draw.
  const secondary: IRound[] =
    format === 'consolation'
      ? // Only the first-round losers play on, so the consolation draw is
        // exactly half the size of the main one.
        blankTree(size / 2, slotPitch, 'C', 'Consolation draw')
      : format === 'triple'
        ? // Second chance is the double-elimination losers bracket; third
          // chance is a shorter tree for whoever survives it with two losses.
          [
            ...losersRoundCounts(size).map((matchCount, idx) => ({
              name: `S${idx + 1}`,
              group: 'Second chance, one loss',
              matches: Array.from({ length: matchCount }, (_, m) => {
                const centre = m * (slotPitch * 3) + slotPitch;
                return {
                  slots: [
                    { label: '', isBye: false, seed: null },
                    { label: '', isBye: false, seed: null },
                  ] as [ISlot, ISlot],
                  centre,
                  lineYs: [centre - slotPitch / 2, centre + slotPitch / 2] as [
                    number,
                    number,
                  ],
                };
              }),
            })),
            ...blankTree(
              Math.max(2, size / 4),
              slotPitch,
              'T',
              'Third chance, two losses'
            ),
          ]
        : [];

  const losersRounds: IRound[] =
    format === 'double'
      ? losersRoundCounts(size).map((matchCount, idx) => ({
          name: `L${idx + 1}`,
          group: 'Losers bracket',
          matches: Array.from({ length: matchCount }, (_, m) => {
            // A standalone blank grid. The real losers structure alternates
            // between "two losers meet" and "a losers winner meets a fresh
            // drop-down from the winners side", which cannot be drawn as one
            // simple column chain, so no inter-round connectors are drawn here.
            // For a sheet whose every line is blank anyway, labelled columns
            // read more clearly than a wrong diagram.
            const centre = m * (slotPitch * 3) + slotPitch;
            return {
              slots: [
                { label: '', isBye: false, seed: null },
                { label: '', isBye: false, seed: null },
              ] as [ISlot, ISlot],
              centre,
              lineYs: [centre - slotPitch / 2, centre + slotPitch / 2] as [
                number,
                number,
              ],
            };
          }),
        }))
      : secondary;

  /**
   * Both of these were wrong before and both clipped the sheet.
   *
   * A 16-player double-elimination draw has SIX losers rounds, so its bracket
   * is WIDER than the five-column winners bracket above it. And the losers
   * matches are pitched three slots apart, not two, so the height has to be
   * measured from the real spacing rather than assumed.
   */
  // Page one holds the winners bracket plus the champion column; page two, when
  // there is one, holds the losers rounds. Each is sized on its own terms, and
  // the wider of the two decides the column width so both pages match.
  const winnersColumns = roundCount + 1;
  const winnersContentH =
    (thirdPlace ? height + slotPitch * 1.5 + slotPitch : height) + 90;

  const colW = columnWidthForPage(
    winnersColumns,
    winnersContentH,
    pageAspect || 277 / 190
  );

  const losersWidth = losersRounds.length
    ? losersRounds.length * (colW + GUTTER)
    : 0;

  const losersHeight = losersRounds.length
    ? Math.max(
        ...losersRounds.map(
          (r) => (r.matches.length - 1) * slotPitch * 3 + slotPitch * 2
        )
      ) + slotPitch
    : 0;

  return {
    rounds,
    losersRounds,
    championCentre,
    width: Math.max(winnersColumns * (colW + GUTTER), losersWidth),
    height,
    losersHeight,
    slotPitch,
    colW,
    thirdPlaceCentre: thirdPlace ? height + slotPitch * 1.5 : null,
  };
}

/* ------------------------------------------------------------------ *
 * The mirrored sheet
 *
 * ⚠️ Added 2026-09-22 for March Madness, the second EVENT sheet and the
 * first draw the straight left-to-right tree could not DRAW, as opposed to
 * could not seed. The NFL sheet needed a slot map; this one needs a second
 * layout entirely.
 *
 * A March Madness bracket is four regions of sixteen that converge on a Final
 * Four in the middle of the page: two regions run left to right down the left
 * half, two run right to left down the right half, and the national title line
 * sits in the dead centre. Drawn as one 64-slot tree running left to right it
 * is arithmetically correct and instantly wrong to anyone who has ever filled
 * one in, and this is the most recognisable sheet in American sport.
 *
 * It also prints better, which was not the reason for doing it but is worth
 * writing down. Sixty-four rows on one side of the page force the pitch down to
 * 16 units and the text to about 5.3pt. Split in half, each side carries 32
 * rows at a 24-unit pitch, and the sheet prints at roughly 8pt on the same A4
 * landscape page.
 * ------------------------------------------------------------------ */

/**
 * Slot order inside one region, as seed numbers.
 *
 * ⚠️ This is NOT `seedOrder(16)` and must never be replaced by it. Repeated
 * reflection gives 1-16, 8-9, 4-13, 5-12, 2-15, 7-10, 3-14, 6-11. The NCAA
 * pairs the 5-12 game above the 4-13 game and puts the 6-11 half above the
 * 7-10 half, which is why a real bracket reads 1, 16, 8, 9, 5, 12, 4, 13 down
 * the page. Both are legal seedings; only one is the one people recognise, and
 * the 5-12 upset is famous precisely because of where it sits on the sheet.
 */
export const NCAA_REGION_ORDER = [
  1, 16, 8, 9, 5, 12, 4, 13, 6, 11, 3, 14, 7, 10, 2, 15,
];

export interface IMirroredPlan {
  /**
   * The four region captions, in the order entrants are typed: the two that
   * run down the left half first, then the two down the right half.
   */
  regionLabels: [string, string, string, string];
  /** Seed order within one region. See NCAA_REGION_ORDER. */
  regionOrder: number[];
  /** Column headers from the outside of the page inward, one per round. */
  roundNames: string[];
  /** Caption over the line in the dead centre. */
  championLabel: string;
}

export interface IMirroredLayout {
  /** Rounds for the left half, outermost column first. */
  left: IRound[];
  /** Rounds for the right half, outermost column first. They draw leftward. */
  right: IRound[];
  /** Y of the single champion line in the centre. */
  championCentre: number;
  /** Y centre of each region caption, in `regionLabels` order. */
  regionCentres: [number, number, number, number];
  width: number;
  height: number;
  slotPitch: number;
  colW: number;
  /** Columns across the whole sheet: both halves plus the centre. */
  columns: number;
  /** X of the champion column. */
  centreX: number;
  /** How many entrants the sheet actually holds. */
  capacity: number;
}

/**
 * Lays the four regions onto one half's first-round lines.
 *
 * Entrants are typed region by region, sixteen at a time, in seed order: the
 * first name is the 1 seed of the first region. The slot order inside each
 * region is the bracket's, not the typist's, which is the whole point of a
 * plan: nobody should have to know that the 8 seed goes on line three.
 */
function placeMirroredHalf(
  entrants: IEntrant[],
  plan: IMirroredPlan,
  fill: TFillMode,
  firstRegion: number
): ISlot[] {
  const perRegion = plan.regionOrder.length;
  const slots: ISlot[] = [];

  for (let r = 0; r < 2; r += 1) {
    const regionBase = (firstRegion + r) * perRegion;
    for (const seed of plan.regionOrder) {
      if (fill === 'blank') {
        slots.push({ label: '', isBye: false, seed: null });
        continue;
      }
      if (fill === 'seeds') {
        slots.push({ label: '', isBye: false, seed });
        continue;
      }
      const entrant = entrants[regionBase + (seed - 1)];
      // A missing name is an empty line to write on, never a bye: a 64-team
      // field has no byes, so the line is simply one the visitor has not
      // filled in yet.
      slots.push({ label: entrant?.name ?? '', isBye: false, seed });
    }
  }

  return slots;
}

export function buildMirroredLayout({
  entrants,
  plan,
  fill = 'names',
  pageAspect,
}: {
  entrants: IEntrant[];
  plan: IMirroredPlan;
  fill?: TFillMode;
  pageAspect?: number;
}): IMirroredLayout {
  const perRegion = plan.regionOrder.length;
  const perHalf = perRegion * 2;
  const capacity = perRegion * 4;

  // The pitch is set by the rows on ONE side, not by the size of the field.
  // This is the whole legibility win: 32 rows, not 64.
  const slotPitch = slotPitchFor(perHalf as TBracketSize);

  const nameFor = (r: number) => plan.roundNames[r] ?? '';
  const left = buildTree(
    placeMirroredHalf(entrants, plan, fill, 0),
    slotPitch,
    nameFor
  );
  const right = buildTree(
    placeMirroredHalf(entrants, plan, fill, 2),
    slotPitch,
    nameFor
  );

  const height = perHalf * slotPitch;

  // Rounds on each side, plus the champion column between them.
  const columns = left.rounds.length * 2 + 1;
  const colW = columnWidthForPage(
    columns,
    height + 90,
    pageAspect || 277 / 190,
    COL_W_MIRRORED_MIN
  );

  const step = colW + GUTTER;
  const centreX = left.rounds.length * step;

  return {
    left: left.rounds,
    right: right.rounds,
    // Both halves finish at the same height, which is exactly what makes the
    // centre work: the two feeds arrive on the champion line from opposite
    // sides rather than needing to be bent around each other.
    championCentre: left.championCentre,
    regionCentres: [
      height / 4,
      (height * 3) / 4,
      height / 4,
      (height * 3) / 4,
    ],
    width: (columns - 1) * step + colW,
    height,
    slotPitch,
    colW,
    columns,
    centreX,
    capacity,
  };
}

/** Splits a pasted block into clean entrant names, capped and de-blanked. */
export function parseEntrantList(raw: string): string[] {
  return raw
    .split(/\r?\n|,/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, MAX_ENTRANTS);
}

/* ------------------------------------------------------------------ *
 * Paper
 *
 * The sheet is the product, so the paper is part of the model, not a
 * styling afterthought. Every number below is millimetres.
 * ------------------------------------------------------------------ */

export type TPaperId = 'a4' | 'letter';
export type TOrientation = 'landscape' | 'portrait';

/** Printer-safe margin. Nothing sold has an unprintable edge wider than this. */
export const PAGE_MARGIN_MM = 10;

export const PAPERS: Record<
  TPaperId,
  { label: string; widthMm: number; heightMm: number }
> = {
  a4: { label: 'A4', widthMm: 210, heightMm: 297 },
  letter: { label: 'Letter', widthMm: 216, heightMm: 279 },
};

export interface IPageBox {
  /** Full sheet, for the `@page size` declaration. */
  sheetWidthMm: number;
  sheetHeightMm: number;
  /** The area actually drawable once margins are taken off. */
  printableWidthMm: number;
  printableHeightMm: number;
}

export function pageBox(paper: TPaperId, orientation: TOrientation): IPageBox {
  const { widthMm, heightMm } = PAPERS[paper];
  const sheetWidthMm = orientation === 'landscape' ? heightMm : widthMm;
  const sheetHeightMm = orientation === 'landscape' ? widthMm : heightMm;

  return {
    sheetWidthMm,
    sheetHeightMm,
    printableWidthMm: sheetWidthMm - PAGE_MARGIN_MM * 2,
    printableHeightMm: sheetHeightMm - PAGE_MARGIN_MM * 2,
  };
}

/**
 * How small a given SVG unit ends up once the drawing is fitted to the page.
 *
 * An SVG with a viewBox and the default `xMidYMid meet` scales to fit INSIDE
 * its box on whichever axis runs out first, so the scale is the smaller of the
 * two ratios. This is what lets us promise the sheet fits exactly: the drawing
 * can never be bigger than the page it is fitted into.
 */
export function fittedScaleMmPerUnit(
  box: IPageBox,
  contentWidth: number,
  contentHeight: number
): number {
  if (contentWidth <= 0 || contentHeight <= 0) return 0;
  return Math.min(
    box.printableWidthMm / contentWidth,
    box.printableHeightMm / contentHeight
  );
}

/** 1pt = 1/72 inch = 0.3528mm. Used to warn before somebody wastes paper. */
export const MM_PER_PT = 25.4 / 72;

/**
 * Below roughly 6pt a printed name is hard to read and impossible to write
 * next to, so the tool says so rather than letting somebody discover it at the
 * printer.
 */
export const MIN_LEGIBLE_PT = 6;
